<script setup lang="ts">
import { computed } from 'vue';

type LayoutVariant = 'standard' | 'workspace';

const props = withDefaults(
  defineProps<{
    layoutClasses: string;
    layoutVariant?: LayoutVariant;
    themeClasses: {
      main: string;
      surface: string;
    };
    showNavigation: boolean;
    showMobileNavigation: boolean;
    isPublicRoute: boolean;
  }>(),
  {
    layoutVariant: 'standard'
  }
);

const shellLayoutClasses = computed(() => ({
  'content-shell__layout--with-navigation': props.showNavigation,
  'content-shell__layout--workspace': props.layoutVariant === 'workspace',
  'content-shell__layout--centered': !props.showNavigation
}));

const contentClasses = computed(() => {
  if (props.isPublicRoute) {
    return 'flex w-full justify-center';
  }

  if (props.layoutVariant === 'workspace') {
    return 'flex min-h-[calc(100vh-7.5rem)] w-full flex-col gap-4';
  }

  return 'flex w-full flex-col gap-5';
});
</script>

<template>
  <div
    class="flex-1"
    :class="[themeClasses.main, layoutVariant === 'workspace' ? 'pb-4 pt-4' : 'pb-8 pt-5 sm:pb-10']"
  >
    <div
      class="content-shell__layout w-full px-4 sm:px-6 lg:px-[34px]"
      :class="[layoutClasses, shellLayoutClasses]"
    >
      <aside
        v-if="showNavigation"
        class="hidden min-w-0 lg:flex lg:flex-col"
      >
        <slot name="navigation" />
      </aside>

      <main
        class="min-w-0"
        :class="contentClasses"
      >
        <section
          v-if="showMobileNavigation"
          id="mobile-menu"
          class="lg:hidden"
        >
          <slot name="mobile-navigation" />
        </section>

        <div
          v-if="isPublicRoute"
          class="flex w-full justify-center"
        >
          <slot />
        </div>
        <div
          v-else
          class="w-full min-w-0"
        >
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
.content-shell__layout {
  display: flex;
  flex-direction: column;
}

.content-shell__layout--centered {
  min-height: calc(100vh - 160px);
  align-items: center;
  justify-content: center;
}

@media (min-width: 1024px) {
  .content-shell__layout--with-navigation {
    display: grid;
    grid-template-columns: 16rem minmax(0, 1fr);
    gap: 1.25rem;
  }

  .content-shell__layout--with-navigation:not(.content-shell__layout--workspace) {
    gap: 2.75rem;
  }
}

@media (min-width: 1280px) {
  .content-shell__layout--with-navigation.content-shell__layout--workspace {
    gap: 1.5rem;
  }
}
</style>
